import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Mail, RefreshCw, CheckCircle, XCircle, AlertCircle, Send, Clock, Zap } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FlowEmailDiagnostics = () => {
  const { isDark, user } = useOutletContext();
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState({});
  const [testTo, setTestTo] = useState('');
  const [filter, setFilter] = useState({ kind: '', status: '' });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [queue, setQueue] = useState(null);
  const [flushing, setFlushing] = useState(false);

  const token = localStorage.getItem('task_token');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, lg, q] = await Promise.all([
        fetch(`${API_URL}/api/tasks-app/flow/diag/email`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API_URL}/api/tasks-app/flow/diag/email-logs?limit=100${filter.kind ? `&kind=${filter.kind}` : ''}${filter.status ? `&status=${filter.status}` : ''}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API_URL}/api/tasks-app/flow/diag/email-queue`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      ]);
      setConfig(cfg);
      setLogs(lg.logs || []);
      setSummary(lg.summary || {});
      setQueue(q);
    } catch (err) {
      console.error('Email diagnostics fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, [token, filter.kind, filter.status]);

  const handleForceFlush = async () => {
    setFlushing(true);
    try {
      const r = await fetch(`${API_URL}/api/tasks-app/flow/diag/email-flush`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(`Flushed: sent ${d.sent}, failed ${d.failed}, remaining ${d.remaining}`);
        fetchAll();
      } else { toast.error(d.detail || 'Flush failed'); }
    } catch (err) { toast.error('Network error'); }
    finally { setFlushing(false); }
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleTestSend = async () => {
    if (!testTo.trim()) { toast.error('Enter a destination email'); return; }
    setSending(true);
    try {
      const r = await fetch(`${API_URL}/api/tasks-app/flow/diag/email-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ to: testTo.trim() }),
      });
      const d = await r.json();
      if (d.sent) toast.success(`Test email sent to ${testTo}`);
      else toast.error('Test email NOT sent — check Logs below for the reason');
      fetchAll();
    } catch (err) { toast.error('Failed: ' + (err.message || 'unknown')); }
    finally { setSending(false); }
  };

  if (user?.role !== 'admin') {
    return <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Admin only.</p>;
  }

  const cardCls = isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200';
  const inputCls = isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-white border-gray-300';

  const statusBadge = (st) => {
    const colors = {
      sent: isDark ? 'bg-green-500/15 text-green-300 border-green-500/30' : 'bg-green-100 text-green-800 border-green-300',
      failed: isDark ? 'bg-red-500/15 text-red-300 border-red-500/30' : 'bg-red-100 text-red-800 border-red-300',
      skipped: isDark ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' : 'bg-amber-100 text-amber-800 border-amber-300',
      queued: isDark ? 'bg-blue-500/15 text-blue-300 border-blue-500/30' : 'bg-blue-100 text-blue-800 border-blue-300',
    };
    const icons = { sent: <CheckCircle size={11} />, failed: <XCircle size={11} />, skipped: <AlertCircle size={11} />, queued: <Clock size={11} /> };
    return (
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase border ${colors[st] || colors.skipped}`}>
        {icons[st]} {st}
      </span>
    );
  };

  const reasonLabel = {
    no_recipient_email: 'No recipient email on this SO',
    no_settings_doc: 'No notification_settings configured',
    email_enabled_false: 'Email is disabled in Settings',
    smtp_not_configured: 'SMTP host/email/password missing',
  };

  return (
    <div className="space-y-6" data-testid="email-diag-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Email Diagnostics</h1>
          <p className={`text-xs mt-0.5 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Why did the customer / sales agent not receive an email?</p>
        </div>
        <Button onClick={fetchAll} variant="ghost" className={`h-8 px-3 ${isDark ? 'text-white/70 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-100'}`}>
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      </div>

      {/* Config card */}
      <Card className={cardCls}>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Mail size={16} className={isDark ? 'text-white/70' : 'text-gray-700'} />
            <h2 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>SMTP Configuration</h2>
          </div>
          {!config ? (
            <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Loading…</p>
          ) : config.configured ? (
            <div className={`text-xs space-y-1 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
              <div>✅ SMTP configured: <b>{config.smtp_email}</b> via <b>{config.smtp_host}</b>:{config.smtp_port}</div>
              <div className={`text-[10px] ${isDark ? 'text-white/30' : 'text-gray-400'}`}>Password: {config.has_password ? 'set' : 'MISSING'}</div>
            </div>
          ) : (
            <div className="text-xs text-red-400">
              ❌ <b>SMTP NOT configured.</b> {config.reason || 'Go to Tasks → Settings → Notifications and save your SMTP details.'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Queue card — emails deferred to next business window */}
      <Card className={cardCls} data-testid="email-queue-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <h2 className={`text-sm font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <Clock size={14} /> Queued for next business window
            </h2>
            {queue?.queued_count > 0 && (
              <Button onClick={handleForceFlush} disabled={flushing} size="sm" className="h-7 px-3 text-xs bg-amber-600 hover:bg-amber-700" data-testid="email-flush-btn" title="Bypass business-hour gate and send everything now">
                <Zap size={12} className="mr-1" /> {flushing ? 'Sending…' : 'Force flush now'}
              </Button>
            )}
          </div>
          {!queue ? (
            <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Loading…</p>
          ) : (
            <>
              <div className={`text-xs mb-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                Window: <b>09:30–18:30 IST · Mon–Sat · holidays excluded</b>.{' '}
                Status:{' '}
                {queue.in_business_window
                  ? <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${isDark ? 'bg-green-500/15 text-green-300' : 'bg-green-100 text-green-700'}`}>WINDOW OPEN</span>
                  : <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${isDark ? 'bg-red-500/15 text-red-300' : 'bg-red-100 text-red-700'}`}>WINDOW CLOSED</span>}
                {!queue.in_business_window && (
                  <span className="ml-2">— Next open: <b>{new Date(queue.next_window_opens_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' })}</b> IST</span>
                )}
              </div>
              {queue.queued_count === 0 ? (
                <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>No emails queued.</p>
              ) : (
                <div className="space-y-1.5 max-h-60 overflow-y-auto">
                  {queue.queued.map(q => (
                    <div key={q.id} className={`flex items-center justify-between gap-2 text-[11px] px-2 py-1.5 rounded ${isDark ? 'bg-white/[0.03] border border-white/5' : 'bg-gray-50 border border-gray-100'}`}>
                      <div className="min-w-0">
                        <div className={`truncate ${isDark ? 'text-white/80' : 'text-gray-800'}`}><b>{q.kind}</b>{q.so_number ? ` · ${q.so_number}` : ''} → {q.to}</div>
                        <div className={`truncate ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{q.subject}</div>
                      </div>
                      <div className={`text-right shrink-0 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                        <div>{new Date(q.scheduled_for).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</div>
                        {q.attempts > 0 && <div className={`text-[9px] ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>retries: {q.attempts}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Send Test card */}
      <Card className={cardCls}>
        <CardContent className="p-4">
          <h2 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Send a Test Email</h2>
          <div className="flex gap-2">
            <Input className={`flex-1 h-9 ${inputCls}`}
              placeholder="your@email.com"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              data-testid="diag-test-to"
            />
            <Button onClick={handleTestSend} disabled={sending} className="h-9 bg-blue-600 hover:bg-blue-700" data-testid="diag-test-send">
              <Send size={14} className="mr-2" /> Send Test
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary + Logs */}
      <Card className={cardCls}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Recent email attempts</h2>
            <div className="flex items-center gap-3 text-xs">
              <span className={isDark ? 'text-green-300' : 'text-green-700'}>✓ Sent: <b>{summary.sent || 0}</b></span>
              <span className={isDark ? 'text-red-300' : 'text-red-700'}>✗ Failed: <b>{summary.failed || 0}</b></span>
              <span className={isDark ? 'text-amber-300' : 'text-amber-700'}>⚠ Skipped: <b>{summary.skipped || 0}</b></span>
            </div>
          </div>
          <div className="flex gap-2 mb-3 flex-wrap">
            <select value={filter.kind} onChange={(e) => setFilter(f => ({ ...f, kind: e.target.value }))} className={`h-8 px-2 rounded text-xs ${inputCls}`}>
              <option value="">All kinds</option>
              <option value="dispatch">Dispatch</option>
              <option value="delivery">Delivery</option>
              <option value="hold_reject">Hold / Reject</option>
              <option value="test">Test</option>
            </select>
            <select value={filter.status} onChange={(e) => setFilter(f => ({ ...f, status: e.target.value }))} className={`h-8 px-2 rounded text-xs ${inputCls}`}>
              <option value="">All statuses</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="skipped">Skipped</option>
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <th className={`text-left py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>When</th>
                  <th className={`text-left py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Kind</th>
                  <th className={`text-left py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Status</th>
                  <th className={`text-left py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>To / CC</th>
                  <th className={`text-left py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>SO</th>
                  <th className={`text-left py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Reason / Error</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className={`py-6 text-center ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Loading…</td></tr>
                ) : logs.length === 0 ? (
                  <tr><td colSpan="6" className={`py-6 text-center ${isDark ? 'text-white/40' : 'text-gray-500'}`}>No email logs yet. Perform a dispatch / delivery to populate.</td></tr>
                ) : logs.map((log) => (
                  <tr key={log.id} className={`border-b ${isDark ? 'border-white/5 hover:bg-white/[0.02]' : 'border-gray-100 hover:bg-gray-50'}`}>
                    <td className={`py-2 px-2 whitespace-nowrap ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{log.created_at?.replace('T', ' ').slice(0, 16)}</td>
                    <td className={`py-2 px-2 capitalize ${isDark ? 'text-white/80' : 'text-gray-800'}`}>{log.kind}</td>
                    <td className="py-2 px-2">{statusBadge(log.status)}</td>
                    <td className={`py-2 px-2 ${isDark ? 'text-white/80' : 'text-gray-800'}`}>
                      <div>{log.to_email || <span className={isDark ? 'text-red-300' : 'text-red-600'}>— no recipient —</span>}</div>
                      {log.cc_emails?.length > 0 && (
                        <div className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>cc: {log.cc_emails.join(', ')}</div>
                      )}
                    </td>
                    <td className={`py-2 px-2 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>{log.so_number || '-'}</td>
                    <td className={`py-2 px-2 max-w-md`}>
                      {log.status === 'sent' ? (
                        <span className={isDark ? 'text-green-300' : 'text-green-700'}>delivered to SMTP</span>
                      ) : log.status === 'skipped' ? (
                        <span className={isDark ? 'text-amber-300' : 'text-amber-700'}>{reasonLabel[log.reason] || log.reason || '—'}</span>
                      ) : (
                        <span className={`${isDark ? 'text-red-300' : 'text-red-700'} text-[10px]`}>{log.error || log.reason || 'unknown error'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FlowEmailDiagnostics;
